import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  IconButton,
  Input,
  Loading,
  Modal,
  PageHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { type Customer, type FineCharge, type LoyaltyLevel } from '@/lib/config'
import { money, dateTime } from '@/lib/format'
import { Cur } from '@/lib/currency'

export function Customers(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Конфигурация · CRM"
        title="Клиенты"
        lead="База клиентов клуба: контакты, карта/QR, уровень лояльности, персональная скидка и бонусный баланс."
      />
      <ClubGate>
        <CustomersTable />
      </ClubGate>
    </>
  )
}

function CustomersTable(): React.JSX.Element {
  const query = useConfigQuery()
  const [q, setQ] = useState('')
  // Список зависит от строки поиска и выбранного клуба.
  const listQuery = { ...(query ?? {}), q: q.trim() || undefined }
  const list = useFetch<Customer[]>('/v1/customers', listQuery, [query?.clubId, q])
  const levels = useFetch<LoyaltyLevel[]>('/v1/loyalty/levels', query, [query?.clubId])
  const [editing, setEditing] = useState<Customer | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Customer | null>(null)
  const [adjusting, setAdjusting] = useState<Customer | null>(null)
  const [fining, setFining] = useState<Customer | null>(null)
  const [visiting, setVisiting] = useState<Customer | null>(null)
  const del = useMutate()

  const rows = list.data ?? []
  const levelList = levels.data ?? []
  const levelName = (id?: string | null): string => levelList.find((l) => l.id === id)?.name ?? '—'

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/customers/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <input
            className="input search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск: имя, телефон, карта…"
          />
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый клиент
          </Button>
        </div>
      </div>

      <AsyncState query={list} />
      {!list.loading && !list.error && rows.length === 0 && (
        <EmptyState message={q ? 'Ничего не найдено.' : 'Клиенты ещё не добавлены.'} />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Телефон</th>
                <th>Уровень</th>
                <th className="num">Перс. скидка</th>
                <th className="num">Бонусы</th>
                <th style={{ width: 236 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.phone || '—'}</td>
                  <td>{levelName(c.levelId)}</td>
                  <td className="num">
                    {c.personalDiscountPercent ? `${c.personalDiscountPercent} %` : '—'}
                  </td>
                  <td className="num">
                    {money(c.bonusBalance ?? 0)} <Cur />
                  </td>
                  <td>
                    <div className="row-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Изменить бонусы"
                        onClick={() => setAdjusting(c)}
                      >
                        Бонусы
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="История посещений"
                        onClick={() => setVisiting(c)}
                      >
                        Посещения
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Штрафы клиента"
                        onClick={() => setFining(c)}
                      >
                        Штрафы
                      </Button>
                      <IconButton title="Изменить" onClick={() => setEditing(c)}>
                        {Icon.edit}
                      </IconButton>
                      <IconButton danger title="Удалить" onClick={() => setDeleting(c)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CustomerForm
          customer={editing === 'new' ? null : editing}
          levels={levelList}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {adjusting && (
        <AdjustBonusForm
          customer={adjusting}
          query={query}
          onClose={() => setAdjusting(null)}
          onSaved={() => {
            setAdjusting(null)
            list.reload()
          }}
        />
      )}

      {visiting && (
        <CustomerVisitsModal
          customer={visiting}
          query={query}
          onClose={() => setVisiting(null)}
        />
      )}

      {fining && (
        <CustomerFinesModal customer={fining} query={query} onClose={() => setFining(null)} />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить клиента"
          message={`Удалить клиента «${deleting.name}»? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function CustomerForm({
  customer,
  levels,
  query,
  onClose,
  onSaved
}: {
  customer: Customer | null
  levels: LoyaltyLevel[]
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = customer !== null
  const [name, setName] = useState(customer?.name ?? '')
  const [phone, setPhone] = useState(customer?.phone ?? '')
  const [cardCode, setCardCode] = useState(customer?.cardCode ?? '')
  const [qrCode, setQrCode] = useState(customer?.qrCode ?? '')
  const [levelId, setLevelId] = useState(customer?.levelId ?? '')
  const [discount, setDiscount] = useState(
    customer?.personalDiscountPercent != null ? String(customer.personalDiscountPercent) : ''
  )
  const [discountNote, setDiscountNote] = useState(customer?.personalDiscountNote ?? '')
  const [discountBarEnabled, setDiscountBarEnabled] = useState(
    customer?.personalDiscountBarEnabled ?? false
  )
  const [notes, setNotes] = useState(customer?.notes ?? '')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите имя клиента.')
    const discNum = discount.trim() === '' ? null : Number(discount)
    if (discNum !== null && (!Number.isFinite(discNum) || discNum < 0 || discNum > 100))
      return setValidation('Персональная скидка — процент от 0 до 100.')
    setValidation(null)

    const body = {
      name: trimmed,
      phone: phone.trim() || null,
      cardCode: cardCode.trim() || null,
      qrCode: qrCode.trim() || null,
      notes: notes.trim() || null,
      levelId: levelId || null,
      personalDiscountPercent: discNum,
      personalDiscountNote: discountNote.trim() || null,
      personalDiscountBarEnabled: discountBarEnabled
    }

    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/customers/${customer.id}`, body, { query })
        : api.post('/v1/customers', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить клиента' : 'Новый клиент'}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="customer-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="customer-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Имя" htmlFor="c-name" full>
            <Input
              id="c-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Анна Сидорова"
              autoFocus
            />
          </Field>
          <Field label="Телефон" htmlFor="c-phone">
            <Input
              id="c-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+7…"
            />
          </Field>
          <Field label="Уровень лояльности" htmlFor="c-level">
            <Select id="c-level" value={levelId} onChange={(e) => setLevelId(e.target.value)}>
              <option value="">— без уровня —</option>
              {levels.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Код карты" htmlFor="c-card">
            <Input id="c-card" value={cardCode} onChange={(e) => setCardCode(e.target.value)} />
          </Field>
          <Field label="QR-код" htmlFor="c-qr">
            <Input id="c-qr" value={qrCode} onChange={(e) => setQrCode(e.target.value)} />
          </Field>
          <Field label="Перс. скидка, %" htmlFor="c-disc">
            <Input
              id="c-disc"
              type="number"
              min={0}
              max={100}
              step="any"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="нет"
            />
          </Field>
          <Field label="Причина скидки" htmlFor="c-disc-note">
            <Input
              id="c-disc-note"
              value={discountNote}
              onChange={(e) => setDiscountNote(e.target.value)}
              placeholder="комментарий"
            />
          </Field>
          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={discountBarEnabled}
                onChange={(e) => setDiscountBarEnabled(e.target.checked)}
              />
              Распространять персональную скидку на бар/кухню
            </label>
          </div>
          <Field label="Заметки" htmlFor="c-notes" full>
            <textarea
              id="c-notes"
              className="input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

function AdjustBonusForm({
  customer,
  query,
  onClose,
  onSaved
}: {
  customer: Customer
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const num = Number(amount)
    if (!Number.isFinite(num) || num === 0)
      return setValidation('Введите ненулевую сумму (отрицательную — для списания).')
    setValidation(null)

    const ok = await save.mutate(() =>
      api.post(
        `/v1/customers/${customer.id}/adjust-bonus`,
        {
          amount: num,
          note: note.trim() || undefined
        },
        { query }
      )
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={`Бонусы · ${customer.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="bonus-form">
            Применить
          </Button>
        </>
      }
    >
      <p className="modal__text" style={{ marginBottom: 16 }}>
        Текущий баланс:{' '}
        <strong>
          {money(customer.bonusBalance ?? 0)} <Cur />
        </strong>
        . Введите сумму корректировки: положительную для начисления, отрицательную для списания.
      </p>
      <form id="bonus-form" onSubmit={(e) => void onSubmit(e)}>
        <Field
          label={
            <>
              Сумма, <Cur />
            </>
          }
          htmlFor="b-amount"
        >
          <Input
            id="b-amount"
            type="number"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="напр. 500 или -200"
            autoFocus
          />
        </Field>
        <Field label="Комментарий" htmlFor="b-note">
          <Input
            id="b-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="причина корректировки"
          />
        </Field>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

/** Штрафы клиента — только чтение (синк терминала). */
/** Ответ GET /v1/customers/:id/visits — игры и брони клиента одной лентой. */
interface CustomerVisits {
  games: {
    id: string
    startedAt: number
    endedAt: number | null
    amount: number
    discountAmount: number
    bonusUsed: number
    bonusEarned: number
    tableName: string | null
    tariffName: string | null
  }[]
  bookings: {
    id: string
    startsAt: number
    endsAt: number
    status: string
    source: string | null
    tableName: string | null
  }[]
  total: number
  spent: number
  lastVisitAt: number | null
}

const BOOKING_STATUS_LABEL: Record<string, string> = {
  pending: 'Ожидает',
  confirmed: 'Подтверждена',
  active: 'Идёт',
  completed: 'Состоялась',
  cancelled: 'Отменена',
  no_show: 'Не пришёл'
}

const BOOKING_SOURCE_LABEL: Record<string, string> = {
  terminal: 'Терминал',
  public: 'Сайт',
  backoffice: 'Бэкофис',
  telegram: 'Telegram'
}

/**
 * История посещений: партии и брони в одной ленте по убыванию времени.
 * Бронь — такой же визит, просто запланированный, поэтому разносить их по
 * разным таблицам значит заставлять читателя складывать историю в уме.
 */
function CustomerVisitsModal({
  customer,
  query,
  onClose
}: {
  customer: Customer
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
}): React.JSX.Element {
  const visits = useFetch<CustomerVisits>(`/v1/customers/${customer.id}/visits`, query, [
    query?.clubId,
    customer.id
  ])
  const data = visits.data
  const feed = [
    ...(data?.games ?? []).map((game) => ({ kind: 'game' as const, ts: game.startedAt, game })),
    ...(data?.bookings ?? []).map((booking) => ({
      kind: 'booking' as const,
      ts: booking.startsAt,
      booking
    }))
  ].sort((a, b) => b.ts - a.ts)

  return (
    <Modal
      title={`Посещения · ${customer.name}`}
      onClose={onClose}
      wide
      footer={
        <Button variant="ghost" onClick={onClose} type="button">
          Закрыть
        </Button>
      }
    >
      {visits.loading ? (
        <Loading />
      ) : visits.error ? (
        <ErrorState message={visits.error} />
      ) : feed.length === 0 ? (
        <EmptyState message="Посещений пока нет: клиент ещё не играл и не бронировал." />
      ) : (
        <>
          <p className="modal__text" style={{ marginBottom: 16 }}>
            Всего посещений: <strong>{data?.total ?? 0}</strong> (игр{' '}
            <strong>{data?.games.length ?? 0}</strong>, броней{' '}
            <strong>{data?.bookings.length ?? 0}</strong>). Последнее —{' '}
            <strong>{data?.lastVisitAt ? dateTime(data.lastVisitAt) : '—'}</strong>, всего оставил{' '}
            <strong>
              {money(data?.spent ?? 0)} <Cur />
            </strong>
            .
          </p>
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Тип</th>
                  <th>Стол</th>
                  <th>Подробности</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {feed.map((v) =>
                  v.kind === 'game' ? (
                    <tr key={`s${v.game.id}`}>
                      <td>{dateTime(v.ts)}</td>
                      <td>Игра</td>
                      <td>{v.game.tableName ?? '—'}</td>
                      <td>
                        {v.game.tariffName ?? '—'}
                        {v.game.discountAmount > 0 && ` · скидка ${money(v.game.discountAmount)}`}
                        {v.game.bonusUsed > 0 && ` · бонусами ${money(v.game.bonusUsed)}`}
                      </td>
                      <td className="num">
                        {money(v.game.amount)} <Cur />
                      </td>
                    </tr>
                  ) : (
                    <tr key={`b${v.booking.id}`}>
                      <td>{dateTime(v.ts)}</td>
                      <td>Бронь</td>
                      <td>{v.booking.tableName ?? '—'}</td>
                      <td>
                        {BOOKING_STATUS_LABEL[v.booking.status] ?? v.booking.status}
                        {v.booking.source &&
                          ` · ${BOOKING_SOURCE_LABEL[v.booking.source] ?? v.booking.source}`}
                      </td>
                      <td className="num">—</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  )
}

function CustomerFinesModal({
  customer,
  query,
  onClose
}: {
  customer: Customer
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
}): React.JSX.Element {
  const fines = useFetch<FineCharge[]>(`/v1/customers/${customer.id}/fines`, query, [
    query?.clubId,
    customer.id
  ])
  const rows = fines.data ?? []
  const total = rows.reduce((s, f) => s + f.amount, 0)

  return (
    <Modal
      title={`Штрафы · ${customer.name}`}
      onClose={onClose}
      footer={
        <Button variant="ghost" onClick={onClose} type="button">
          Закрыть
        </Button>
      }
    >
      {fines.loading ? (
        <Loading />
      ) : fines.error ? (
        <ErrorState message={fines.error} />
      ) : rows.length === 0 ? (
        <EmptyState message="Клиента ещё не штрафовали на терминале." />
      ) : (
        <>
          <p className="modal__text" style={{ marginBottom: 16 }}>
            Всего штрафов: <strong>{rows.length}</strong> на сумму{' '}
            <strong>
              {money(total)} <Cur />
            </strong>
            .
          </p>
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Штраф</th>
                  <th>Комментарий</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id}>
                    <td>{dateTime(f.ts)}</td>
                    <td>{f.name}</td>
                    <td>{f.note ?? '—'}</td>
                    <td className="num">
                      {money(f.amount)} <Cur />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  )
}
